/** Thumnail image generation based on https://github.com/mozilla/pdf.js/blob/master/web/pdf_thumbnail_view.js **/

const DRAW_UPSCALE_FACTOR = 2; // See comment in `PDFThumbnailView.draw` below.
const MAX_NUM_SCALING_STEPS = 3;
const THUMBNAIL_WIDTH = 130; // px

export function createThumbnailImageFromRenderedCanvas(pdfPage, pageCanvas) {
  /** IMPORTANT: Make sure you call this with a canvas rendered with unscaled (1:1) pdf page **/
  const pageViewport = pdfPage.getViewport({ scale: 1 });
  const pageWidth = pageViewport.width;
  const pageHeight = pageViewport.height;
  const heightOverWidth = pageHeight / pageWidth;

  if (Math.abs(pageCanvas.width - pageWidth) > 2) {
    // allow for small rounding discrepancy
    // we simplified downscale logic by making assumption that we're working against a 1:1 scale
    console.error(
      "createThumbnailImageFromRenderedCanvas should be called before pageCanvas is rescaled",
    );
  }

  const outputScale = window.devicePixelRatio || 1;
  const targetWidth = THUMBNAIL_WIDTH * DRAW_UPSCALE_FACTOR * outputScale;
  const targetHeight = targetWidth * heightOverWidth;

  const imageCanvas = downscaleCanvas(pageCanvas, targetWidth, targetHeight);
  const dataUrl = imageCanvas.toDataURL();

  freeCanvas(imageCanvas);
  return {
    src: dataUrl,
    width: THUMBNAIL_WIDTH,
    height: THUMBNAIL_WIDTH * heightOverWidth,
  };
}

function downscaleCanvas(pageCanvas, width, height) {
  const imageCanvas = document.createElement("canvas");
  imageCanvas.width = width;
  imageCanvas.height = height;
  const imageContext = imageCanvas.getContext("2d", { alpha: false });

  // If target size is >50% of page, no need anything fancy. Just draw it and return;
  if (width * 2 >= pageCanvas.width) {
    imageContext.drawImage(
      pageCanvas,
      0,
      0,
      pageCanvas.width,
      pageCanvas.height,
      0,
      0,
      width,
      height,
    );
    return imageCanvas;
  }

  // If size difference is too big, we need to do the downscaling gradually or results will be awful.

  // We will halve the image size each, so determine size for initial downscale based on TARGET_DIMS * 2^3
  let reducedWidth = width << MAX_NUM_SCALING_STEPS;
  let reducedHeight = height << MAX_NUM_SCALING_STEPS;

  // Temp canvas is sized based on max initial downscale target
  const tempImage = createTempCanvas(reducedWidth, reducedHeight);
  const tempCanvas = tempImage.canvas;
  const tempContext = tempImage.context;

  // Adjust accordingly if initial target is bigger than actual image
  while (reducedWidth > pageCanvas.width || reducedHeight > pageCanvas.height) {
    reducedWidth >>= 1;
    reducedHeight >>= 1;
  }

  // First, draw page canvas onto the temp canvas at initial downscale step
  tempContext.drawImage(
    pageCanvas,
    0,
    0,
    pageCanvas.width,
    pageCanvas.height,
    0,
    0,
    reducedWidth,
    reducedHeight,
  );

  // Next, we repeatedly halve the size by drawing temp image onto until the penultimate downscale step
  while (reducedWidth > width * 2) {
    tempContext.drawImage(
      tempCanvas,
      0,
      0,
      reducedWidth,
      reducedHeight,
      0,
      0,
      reducedWidth >> 1,
      reducedHeight >> 1,
    );
    reducedWidth >>= 1;
    reducedHeight >>= 1;
  }

  // Finally, final the final step gets drawn directly onto the output canvas
  imageContext.drawImage(
    tempCanvas,
    0,
    0,
    reducedWidth,
    reducedHeight,
    0,
    0,
    width,
    height,
  );

  freeCanvas(tempCanvas);
  return imageCanvas;
}

function createTempCanvas(width, height) {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext("2d", { alpha: false });
  context.save(); // save default states so the following fill commands do not impact future context
  context.fillStyle = "rgb(255, 255, 255)";
  context.fillRect(0, 0, width, height);
  context.restore();

  return {
    canvas: canvas,
    context: context,
  };
}

function freeCanvas(canvas) {
  // Zeroing the width and height causes Firefox to release graphics
  // resources immediately, which can greatly reduce memory consumption.
  canvas.width = 0;
  canvas.height = 0;
}
